import React, {useState} from 'react';
import {Button, Spin, Table, Tag, Tooltip, Typography} from "antd";
import {DeleteTwoTone, FlagTwoTone, PlusOutlined, StarTwoTone} from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import AccessKeyAddForm from "./AccessKeyAddForm";
import confirmAction from "../../helpers/confirmAction";
import {ucFirst} from "../../helpers/misc";

import './AccessKey.less'

const columns = [
    {
        title: <Typography.Text strong>Key ID</Typography.Text>,
        dataIndex: 'identifier',
        key: 'identifier',
        render: id => {
            return <span className="key-id-line-item long-text-wrap">{id}</span>
        },
    },
    {
        title: <Typography.Text strong>Type</Typography.Text>,
        dataIndex: 'type',
        key: 'type',
        render: type => {
            return <span className="type-line-item long-text-wrap">
                        {type === 'awid' ? type.toUpperCase() : ucFirst(type)}
                   </span>
        },
    },
    {
        title: <Typography.Text strong>Status</Typography.Text>,
        dataIndex: 'is_blessed',
        key: 'is_blessed',
        render: (element) => (
            <>
                <span className="access-key-status-line-item long-text-wrap">Status:</span>
                <Tag color={element === true ? 'green' : 'default'}>
                    {element === true ? ucFirst('Blessed') : ucFirst('Regular')}
                </Tag>
            </>
        )
    },
    {
        title: <Typography.Text strong>Actions</Typography.Text>,
        dataIndex: 'actions',
        key: 'actions',
        render: (actions, row) => (
            <>
                <span className="access-key-actions-line-item long-text-wrap">Actions:</span>
                {
                    actions.map(action => {
                        switch (action) {
                            case 'delete':
                                return (
                                    <Button className="access-key-icon" type="link" key={action}
                                        icon={
                                            <Tooltip title="Unassign">
                                                <DeleteTwoTone twoToneColor='#1890ff'/>
                                            </Tooltip>
                                        }
                                        onClick={() => confirmAction(
                                          'Are you sure you want to unassign this access key?',
                                          'Members will no longer be able to use this key to access vehicles. ' +
                                          'The same key can always be reassigned to the same user in the future.',
                                          row.deleteFunction,
                                          row.id,
                                          undefined,
                                          'Unassign Access Key',
                                          'Cancel'
                                        )}>
                                    </Button>
                                )
                            case 'bless':
                                return (
                                    <Button className="access-key-icon" type="link" key={action}
                                        icon={
                                            <Tooltip title="Bless/Unbless">
                                                <StarTwoTone twoToneColor='#1890ff'/>
                                            </Tooltip>
                                        }
                                        onClick={() => confirmAction(
                                          `Are you sure you want to ${row.is_blessed ? 'unbless' : 'bless'} this access key?`,
                                          row.is_blessed ? 'When an access key is unblessed, it can only be used during a valid booking.' :
                                          'When an access key is blessed, it can be used to access any car without a booking.',
                                          row.blessFunction,
                                          row,
                                          undefined,
                                          row.is_blessed ? 'Unbless' : 'Bless',
                                          'Cancel'
                                        )}>
                                    </Button>
                                )
                            case 'lost':
                                return (
                                    <Button className="access-key-icon" type="link" key={action}
                                        icon={
                                            <Tooltip title="Report lost/stolen">
                                                <FlagTwoTone twoToneColor='#1890ff'/>
                                            </Tooltip>
                                        }
                                        onClick={() => confirmAction(
                                          'Are you sure you want to report this access key as lost/stolen?',
                                          'This access key will be automatically unassigned if it ' +
                                          'is reported lost/stolen. You may reassign the same key in the future if it is found.',
                                          row.lostFunction,
                                          row.id,
                                          undefined,
                                          'Report Lost/Stolen',
                                          'Cancel'
                                        )}>
                                    </Button>
                                )
                            default:
                                return null;
                        }
                    })
                }
            </>
        )
    }
];

const AccessKeyTable = ({fobs, loading, refreshFobs}) => {
    const [drawerVisible, setDrawerVisible] = useState(false);

    const showDrawer = () => {
        setDrawerVisible(true);
    };

    const hideDrawer = () => {
        setDrawerVisible(false);
    };

    // Filter the fobs by activity (started_at and ended_at contain now).
    const isFobActive = (fob) => {
        return (dayjs(fob.ended_at).tz().isAfter(dayjs().tz()) || fob.ended_at === null);
    }

    return (
        <>
            <Spin spinning={loading}>
                <Table
                    rowKey="id"
                    dataSource={fobs.filter(isFobActive)}
                    columns={columns}
                    className="access-key-table responsive-table"
                    pagination={false}
                    title={() =>
                        <div className="access-key-table-title">
                            <Typography.Text>Access Key</Typography.Text>
                            <Button
                                className="access-key-table-add-btn"
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={showDrawer}
                            >
                                Assign
                            </Button>
                        </div>
                    }
                />
                <AccessKeyAddForm
                    fobs={fobs}
                    drawerVisible={drawerVisible}
                    hideDrawer={hideDrawer}
                    refreshFobs={refreshFobs}
                />
            </Spin>
        </>
    );
}

export default AccessKeyTable

AccessKeyTable.propTypes = {
    fobs: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    refreshFobs: PropTypes.func.isRequired,
};
